import React, { useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  RefreshControl,
} from 'react-native';
import { ACCOUNT_COLORS } from '@/constants/residents/mobile/accountColors';
import { accountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { strings } from '@/constants/residents/strings';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { usePaymentMethodActions } from '@/features/payments/hooks/usePaymentMethodActions';
import { sortPaymentMethodsByDefault } from '@/utils/residents/paymentUtils';
import { PaymentMethodCard } from '@/components/residents/payments/PaymentMethodCard';
import { PaymentFormModal } from '@/screens/residents/payments/mobile/PaymentFormModal';
import { AccountEmptyState } from '@/components/residents/account/AccountEmptyState';

const ChevronRightIcon = RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT;
const EmptyCardIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_CARD;

/**
 * Mobile Payment Methods Section
 * Displays payment methods list with add/remove functionality
 * Based on Figma design "2. Payment"
 */
export const MobilePaymentMethodsSection: React.FC = () => {
  const { paymentMethods, isLoading, refreshPaymentMethods } = usePaymentData();

  // Modal visibility state - unified to use formType
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [formType, setFormType] = useState<'card' | 'ach'>('card');

  const {
    setAsDefault,
    deleteMethodWithConfirmation,
    isUpdating,
    isDeleting,
  } = usePaymentMethodActions(refreshPaymentMethods);

  const sortedMethods = useMemo(() => {
    return sortPaymentMethodsByDefault(paymentMethods);
  }, [paymentMethods]);

  const hasNoMethods = sortedMethods.length === 0 && !isLoading;
  const isOnlyOneMethod = sortedMethods.length === 1;

  // Open credit/debit card form modal
  const handleAddCard = useCallback(() => {
    setFormType('card');
    setShowPaymentForm(true);
  }, []);

  // Open ACH bank transfer form modal
  const handleAddACH = useCallback(() => {
    setFormType('ach');
    setShowPaymentForm(true);
  }, []);

  // Handle payment method added successfully
  const handleMethodAdded = useCallback(() => {
    setShowPaymentForm(false);
    refreshPaymentMethods();
  }, [refreshPaymentMethods]);

  return (
    <View style={accountSectionStyles.container}>
      {/* Header */}
      <View style={accountSectionStyles.header}>
        <Text style={accountSectionStyles.headerTitle}>{strings.account.paymentMethodsTitle}</Text>
      </View>

      {/* Add New Payment Method Section */}
      <View style={styles.addSection}>
        <Text style={styles.addSectionTitle}>{strings.account.paymentMethodsAddTitle}</Text>
        
        <TouchableOpacity style={accountSectionStyles.actionButton} onPress={handleAddCard} activeOpacity={0.8}>
          <Text style={accountSectionStyles.actionButtonText}>{strings.account.paymentMethodsAddCard}</Text>
          <ChevronRightIcon width={24} height={24} color={ACCOUNT_COLORS.textPrimary} />
        </TouchableOpacity>

        <TouchableOpacity style={accountSectionStyles.actionButton} onPress={handleAddACH} activeOpacity={0.8}>
          <Text style={accountSectionStyles.actionButtonText}>{strings.account.paymentMethodsAddACH}</Text>
          <ChevronRightIcon width={24} height={24} color={ACCOUNT_COLORS.textPrimary} />
        </TouchableOpacity>
      </View>

      {/* Payment Methods List or Empty State */}
      <ScrollView 
        style={styles.methodsSection}
        contentContainerStyle={styles.methodsContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={refreshPaymentMethods}
            tintColor={ACCOUNT_COLORS.navActiveBackground}
          />
        }
      >
        {isLoading && sortedMethods.length === 0 ? (
          <View style={accountSectionStyles.loadingContainer}>
            <ActivityIndicator size="large" color={ACCOUNT_COLORS.navActiveBackground} />
          </View>
        ) : hasNoMethods ? (
          <AccountEmptyState
            icon={EmptyCardIcon && <EmptyCardIcon width={64} height={64} color={ACCOUNT_COLORS.textSecondary} />}
            title={strings.account.paymentMethodsEmptyTitle}
            message={strings.account.paymentMethodsEmptyMessage}
            variant="large"
          />
        ) : isOnlyOneMethod ? (
          // Single card - full width layout
          <View style={styles.methodsList}>
            <PaymentMethodCard
              key={sortedMethods[0].id || sortedMethods[0].sourceId || 0}
              method={sortedMethods[0]}
              index={0}
              variant="wallet"
              isOnlyCard={true}
              isExpired={sortedMethods[0].isExpired}
              onSetAsDefault={() => setAsDefault(sortedMethods[0])}
              onDelete={() => deleteMethodWithConfirmation(sortedMethods[0])}
              isUpdating={isUpdating}
              canDelete={!sortedMethods[0].isDefault || isOnlyOneMethod}
              showActions={true}
            />
          </View>
        ) : (
          // Multiple cards - 2-column grid layout (Figma design)
          <View style={styles.methodsGrid}>
            {/* Left column */}
            <View style={styles.methodsColumn}>
              {sortedMethods
                .filter((_, index) => index % 2 === 0)
                .map((method, colIndex) => {
                  const originalIndex = colIndex * 2;
                  return (
                    <PaymentMethodCard
                      key={method.id || method.sourceId || originalIndex}
                      method={method}
                      index={originalIndex}
                      variant="wallet"
                      isOnlyCard={false}
                      isExpired={method.isExpired}
                      onSetAsDefault={() => setAsDefault(method)}
                      onDelete={() => deleteMethodWithConfirmation(method)}
                      isUpdating={isUpdating}
                      canDelete={!method.isDefault}
                      showActions={true}
                    />
                  );
                })}
            </View>
            {/* Right column */}
            <View style={styles.methodsColumn}>
              {sortedMethods
                .filter((_, index) => index % 2 === 1)
                .map((method, colIndex) => {
                  const originalIndex = colIndex * 2 + 1;
                  return (
                    <PaymentMethodCard
                      key={method.id || method.sourceId || originalIndex}
                      method={method}
                      index={originalIndex}
                      variant="wallet"
                      isOnlyCard={false}
                      isExpired={method.isExpired}
                      onSetAsDefault={() => setAsDefault(method)}
                      onDelete={() => deleteMethodWithConfirmation(method)}
                      isUpdating={isUpdating}
                      canDelete={!method.isDefault}
                      showActions={true}
                    />
                  );
                })}
            </View>
          </View>
        )}
      </ScrollView>

      {/* Payment Form Modal - Reuses content components from Make Payment flow */}
      <PaymentFormModal
        visible={showPaymentForm}
        onClose={() => setShowPaymentForm(false)}
        onMethodAdded={handleMethodAdded}
        formType={formType}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // Add Section
  addSection: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 16,
  },
  addSectionTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textPrimary,
    lineHeight: 24,
  },
  // Methods Section
  methodsSection: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: ACCOUNT_COLORS.innerBorder,
  },
  methodsContent: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    flexGrow: 1,
  },
  methodsList: {
    gap: 16,
  },
  // 2-column grid layout for multiple payment methods (Figma design)
  methodsGrid: {
    flexDirection: 'row',
    gap: 16,
  },
  methodsColumn: {
    flex: 1,
    gap: 16,
  },
});

export default MobilePaymentMethodsSection;
